import { useState, useEffect, useCallback, useContext } from 'react';
import {
    Box, Stack, Typography, Button, Chip, Avatar, CircularProgress, IconButton,
} from '@mui/joy';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ArrowBackRoundedIcon  from '@mui/icons-material/ArrowBackRounded';
import RefreshRoundedIcon     from '@mui/icons-material/RefreshRounded';

import { AppContext } from '../../context';
import {
    APIGetPendingSalesOverview,
    APIGetPendingSaleNotifications,
    APIProcessSaleNotification,
    APICancelSaleNotification,
    APIGetProducts, APIGetDiscounts, APIGetProductCategories,
    parseApiError,
} from '../../api';
import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import ProcessSaleModal from '../workstation/ProcessSaleModal';

const fmtMoney = (v) => (parseFloat(v) || 0).toLocaleString('ru-RU');

const getInitials = (name = '') => {
    const p = (name || '').trim().split(' ');
    return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
};

const fmtAge = (min) => {
    if (min == null) return '';
    if (min < 60) return `${min} мин`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} ч ${m} мин` : `${h} ч`;
};

export default function PendingSalesPage() {
    const { token, hasPerm, showToast, onRefreshTick } = useContext(AppContext);

    const [loading,  setLoading]  = useState(true);
    const [overview, setOverview] = useState({ total_pending: 0, locations: [] });

    // Заход внутрь точки
    const [selectedLoc,    setSelectedLoc]    = useState(null); // { location_id, location_name }
    const [pending,        setPending]        = useState([]);
    const [pendingLoading, setPendingLoading] = useState(false);
    const [processingId,   setProcessingId]   = useState(null);

    // Справочники для модалки оформления
    const [products,   setProducts]   = useState([]);
    const [discounts,  setDiscounts]  = useState([]);
    const [categories, setCategories] = useState([]);
    const [processModal, setProcessModal] = useState({ open: false, notification: null, loading: false });

    const canReview = hasPerm('finance.sale_notifications.review');

    // ── Загрузка обзора + справочников ──
    const loadOverview = useCallback(async () => {
        if (!token) return true;
        setLoading(true);
        const [ov, prods, discs, cats] = await Promise.all([
            APIGetPendingSalesOverview(token),
            APIGetProducts(token, { is_active: true }),
            APIGetDiscounts(token, { is_active: true }),
            APIGetProductCategories(token, { is_active: true, with_counts: true }),
        ]);
        if (ov && Array.isArray(ov.locations)) setOverview(ov);
        if (Array.isArray(prods)) setProducts(prods);
        if (Array.isArray(discs)) setDiscounts(discs);
        if (Array.isArray(cats))  setCategories(cats);
        setLoading(false);
        return true;
    }, [token]);

    useEffect(() => { loadOverview(); }, [loadOverview]);
    useEffect(() => onRefreshTick(loadOverview), [onRefreshTick, loadOverview]);

    // ── Заход внутрь точки ──
    const openLocation = useCallback(async (loc) => {
        setSelectedLoc(loc);
        setPendingLoading(true);
        const list = await APIGetPendingSaleNotifications(token, {
            location_id: loc.location_id, status: 'pending',
        });
        setPending(Array.isArray(list) ? list : []);
        setPendingLoading(false);
    }, [token]);

    const backToOverview = () => {
        setSelectedLoc(null);
        setPending([]);
        loadOverview();
    };

    // ── Оформление (payload приходит из ProcessSaleModal) ──
    const handleSubmitProcess = async (payload) => {
        const n = processModal.notification;
        if (!n) return;
        setProcessModal(p => ({ ...p, loading: true }));
        const res = await APIProcessSaleNotification(token, n.id, payload);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось оформить чек'), 'danger');
            setProcessModal(p => ({ ...p, loading: false }));
            return;
        }
        showToast('Чек оформлен ✓');
        setPending(prev => prev.filter(p => p.id !== n.id));
        setProcessModal({ open: false, notification: null, loading: false });
    };

    // ── Подтверждение (чек уже в кассе — здесь только отметка) ──
    const handleConfirm = async (n) => {
        setProcessingId(n.id);
        const res = await APIProcessSaleNotification(token, n.id, {});
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось подтвердить'), 'danger');
            setProcessingId(null);
            return;
        }
        showToast('Продажа подтверждена ✓');
        setPending(prev => prev.filter(p => p.id !== n.id));
        setProcessingId(null);
    };

    // ── Отмена ──
    const handleCancel = async (n) => {
        setProcessingId(n.id);
        const res = await APICancelSaleNotification(token, n.id);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось отменить'), 'danger');
        } else {
            showToast('Чек отменён');
            setPending(prev => prev.filter(p => p.id !== n.id));
        }
        setProcessingId(null);
    };

    if (loading) {
        return (
            <>
                <Header />
                <Stack sx={{ minHeight: '60vh', justifyContent: 'center', alignItems: 'center' }}>
                    <MemeLoader />
                </Stack>
            </>
        );
    }

    return (
        <>
            <Header />
            <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: 3 }}>
                {/* Заголовок */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                    {selectedLoc && (
                        <IconButton size="sm" variant="outlined" onClick={backToOverview}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                    )}
                    <Box sx={{ flex: 1 }}>
                        <Typography level="h4" sx={{ fontWeight: 800 }}>
                            {selectedLoc ? selectedLoc.location_name : 'Висящие чеки'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                            {selectedLoc
                                ? `${pending.length} неоформленных`
                                : `Всего неоформлено: ${overview.total_pending}`}
                        </Typography>
                    </Box>
                    {!selectedLoc && (
                        <IconButton size="sm" variant="outlined" onClick={loadOverview}>
                            <RefreshRoundedIcon />
                        </IconButton>
                    )}
                </Box>

                {/* ── Обзор по точкам ── */}
                {!selectedLoc && (
                    overview.locations.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', py: 6 }}>
                            Нет висящих чеков 🎉
                        </Typography>
                    ) : (
                        <Stack spacing={1.2}>
                            {overview.locations.map(loc => (
                                <Box
                                    key={loc.location_id}
                                    onClick={() => openLocation(loc)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                        p: 1.8, cursor: 'pointer',
                                        border: '1px solid rgba(245,158,11,0.35)',
                                        bgcolor: 'rgba(245,158,11,0.06)',
                                        borderRadius: 'var(--joy-radius-lg)',
                                        transition: 'all .15s',
                                        '&:hover': { borderColor: '#f59e0b', bgcolor: 'rgba(245,158,11,0.1)' },
                                    }}
                                >
                                    <Avatar size="sm" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                                        <StorefrontRoundedIcon sx={{ fontSize: 18 }} />
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                                            {loc.location_name}
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                            {loc.current_admin ? `Админ: ${loc.current_admin}` : 'Смена не начата'}
                                            {loc.oldest_age_minutes != null && ` · ждёт ${fmtAge(loc.oldest_age_minutes)}`}
                                        </Typography>
                                    </Box>
                                    <Chip size="lg" variant="soft" color="warning" sx={{ fontWeight: 800 }}>
                                        {loc.pending_count}
                                    </Chip>
                                </Box>
                            ))}
                        </Stack>
                    )
                )}

                {/* ── Список висящих по выбранной точке ── */}
                {selectedLoc && (
                    pendingLoading ? (
                        <Stack sx={{ alignItems: 'center', py: 6 }}><CircularProgress /></Stack>
                    ) : pending.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', py: 6 }}>
                            На этой точке висящих чеков нет
                        </Typography>
                    ) : (
                        <Stack spacing={1.4}>
                            {pending.map(n => (
                                <Box key={n.id} sx={{
                                    border: '1px solid rgba(245,158,11,0.35)',
                                    bgcolor: 'rgba(245,158,11,0.06)',
                                    borderRadius: 'var(--joy-radius-lg)', p: 1.8,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1 }}>
                                        <Avatar src={n.photographer_avatar_url} size="sm" sx={{ bgcolor: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
                                            {(n.photographer_name || '').charAt(0).toUpperCase()}
                                        </Avatar>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                                {n.photographer_name}
                                            </Typography>
                                            {n.comment && (
                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    {n.comment}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#f59e0b' }}>
                                            {fmtMoney(n.total_value)} ₽
                                        </Typography>
                                    </Box>

                                    {canReview && (
                                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                            <Button
                                                size="sm" variant="solid" color="success" sx={{ flex: 1 }}
                                                loading={processingId === n.id}
                                                onClick={() => handleConfirm(n)}
                                            >
                                                Подтвердить
                                            </Button>
                                            <Button
                                                size="sm" variant="outlined" color="danger"
                                                loading={processingId === n.id}
                                                onClick={() => {
                                                    if (window.confirm('Отменить продажу? Чек будет удалён из кассы, выручка пересчитается.')) {
                                                        handleCancel(n);
                                                    }
                                                }}
                                            >
                                                Отменить
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    )
                )}
            </Box>

            {/* Модалка оформления — та же, что на рабочей станции */}
            <ProcessSaleModal
                open={processModal.open}
                notification={processModal.notification}
                products={products}
                discounts={discounts}
                categories={categories}
                loading={processModal.loading}
                onClose={() => setProcessModal({ open: false, notification: null, loading: false })}
                onSubmit={handleSubmitProcess}
                canSetDate={hasPerm('finance.orders.set_date')}
            />
        </>
    );
}